import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight, ArrowDown, Filter } from 'lucide-react';
import { useFunnelStageConversion, type FunnelFilters } from '@/hooks/useFunnelMetrics';

const STAGE_LABELS: Record<string, string> = {
  new: 'Novo',
  contact: 'Contato',
  scheduled: 'Reunião',
  proposal: 'Proposta',
  negotiation: 'Negociação',
};

const STAGE_COLORS: Record<string, { bg: string; text: string }> = {
  new: { bg: 'bg-blue-500', text: 'text-white' },
  contact: { bg: 'bg-cyan-500', text: 'text-white' },
  scheduled: { bg: 'bg-yellow-500', text: 'text-black' },
  proposal: { bg: 'bg-orange-500', text: 'text-white' },
  negotiation: { bg: 'bg-purple-500', text: 'text-white' },
};

interface Props {
  filters: FunnelFilters;
}

export default function FunnelStageConversion({ filters }: Props) {
  const { data, isLoading } = useFunnelStageConversion(filters);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Funil de Conversão
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const stages = data || [];
  const lastStage = stages.length > 0 ? stages[stages.length - 1] : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Funil de Conversão por Estágio
        </CardTitle>
      </CardHeader>
      <CardContent>
        {stages.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">Sem dados para o período</p>
        ) : (
          <div className="flex items-center justify-center flex-wrap gap-2">
            {stages.map((stage, index) => (
              <div key={stage.stage} className="flex items-center gap-2">
                {/* Stage Box with Lost Indicator */}
                <div className="flex flex-col items-center gap-1">
                  <div className={`flex flex-col items-center p-3 rounded-lg ${STAGE_COLORS[stage.stage]?.bg || 'bg-muted'} ${STAGE_COLORS[stage.stage]?.text || 'text-foreground'} min-w-[100px]`}>
                    <span className="text-xs font-medium opacity-90">
                      {STAGE_LABELS[stage.stage] || stage.stage}
                    </span>
                    <span className="text-2xl font-bold">{stage.total_entered}</span>
                  </div>
                  
                  {/* Lost Indicator */}
                  {stage.went_to_lost > 0 && (
                    <div className="flex items-center gap-1 text-red-500">
                      <ArrowDown className="h-3 w-3" />
                      <span className="text-xs font-medium">{stage.went_to_lost}</span>
                    </div>
                  )}
                </div>
                
                {/* Arrow with conversion rate - show for ALL stages including the last one */}
                <div className="flex flex-col items-center gap-1 px-2">
                  <span className={`text-sm font-semibold ${
                    stage.conversion_rate >= 50 ? 'text-green-600' : 
                    stage.conversion_rate >= 30 ? 'text-yellow-600' : 
                    'text-red-500'
                  }`}>
                    {stage.conversion_rate?.toFixed(0) || 0}%
                  </span>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
            ))}
            
            {/* Won Box */}
            <div className="flex flex-col items-center p-3 rounded-lg bg-green-500 text-white min-w-[100px]">
              <span className="text-xs font-medium opacity-90">Ganhos</span>
              <span className="text-2xl font-bold">
                {lastStage?.advanced_to_next || 0}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
